import React, { forwardRef, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { IconButton } from "@components/IconButton";
import { SearchLg, XClose } from "untitledui-js/react";
import { Loader } from "@components/Loader";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { useConfigContext } from "@hooks/useConfigContext";
import { useMobileSearchActions } from "@hooks/useMobileSearchActions";
import { SearchInputProps } from "../../types/SearchTypes";
import { handleContainerClick } from "../../utils/handleContainerClick";

export const SearchInput = forwardRef<HTMLTextAreaElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      onKeyDown,
      onSend,
      children,
      isHomepage = false,
      mobileIconOnly = false,
      hideMobileIcon = false,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { state, dispatch } = useConfigContext();
    const { closeMobileSearch, openMobileSearch } = useMobileSearchActions();

    // Local state for dropdown animation
    const [showDropdownContent, setShowDropdownContent] = useState(false);
    const { isSending, isStreaming, disabled } = props;

    // Get mobile search state from global context
    const { isMobileSearchOpen } = state;

    // Show dropdown only when focused AND empty (regardless of homepage status)
    const shouldShowDropdown =
      state.isSearchOpen && (value || "").trim().length === 0;

    // Disable button if sending, streaming, or disabled
    const isDisabled = isSending || isStreaming || disabled;

    // Handle dropdown content animation timing (only for non-homepage)
    useEffect(() => {
      const showWithDelay = () => {
        const timer = setTimeout(() => setShowDropdownContent(true), 200);
        return () => clearTimeout(timer);
      };
      const showImmediately = () => {
        setShowDropdownContent(true);
        return () => undefined;
      };
      const hide = () => {
        setShowDropdownContent(false);
        return () => undefined;
      };

      if (shouldShowDropdown) {
        return isHomepage ? showImmediately() : showWithDelay();
      }
      return hide();
    }, [shouldShowDropdown, isHomepage]);

    // Handle Cmd+K keyboard shortcut to focus textarea and Escape to blur
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
        if ((event.metaKey || event.ctrlKey) && event.key === "k") {
          event.preventDefault();
          const textarea = document.getElementById(
            "userPrompt"
          ) as HTMLTextAreaElement;
          if (textarea) {
            textarea.focus();
            dispatch({ type: "SEARCH_VISIBILITY", payload: { visible: true } });
          }
        }
        // Handle Escape key to blur textarea and collapse
        else if (event.key === "Escape") {
          const textarea = document.getElementById(
            "userPrompt"
          ) as HTMLTextAreaElement;
          if (textarea && textarea === document.activeElement) {
            textarea.blur();
            dispatch({
              type: "SEARCH_VISIBILITY",
              payload: { visible: false },
            });
            if (isMobileSearchOpen) {
              closeMobileSearch();
            }
          }
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => {
        // Cleanup event listener
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [dispatch, isMobileSearchOpen, closeMobileSearch]);

    const handleSend = () => {
      if (!(value || "").trim()) return;
      if (isDisabled) return;

      try {
        onSend();
      } catch (error) {
        console.error("Failed to send message:", error);
        Sentry.captureException(error);
      }
    };

    const handleFocus = () => {
      dispatch({ type: "SEARCH_VISIBILITY", payload: { visible: true } });
      // Fire custom event so centered search width can recompute
      window.dispatchEvent(new Event("recompute-centered-search-width"));
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    };

    // Extract nested ternary operation for dropdown animation classes
    const getDropdownAnimationClasses = () => {
      if (!isHomepage && showDropdownContent) {
        return "translate-y-0 opacity-100";
      } else if (!isHomepage) {
        return "-translate-y-2 opacity-0";
      } else {
        return "translate-y-0 opacity-100";
      }
    };

    return (
      <>
        {!isHomepage && mobileIconOnly && !hideMobileIcon && (
          <IconButton
            ariaLabel={
              isMobileSearchOpen
                ? t("OmniSearch.closeSearch")
                : t("OmniSearch.openSearch")
            }
            id="sendPrompt-mobile"
            icon={
              isMobileSearchOpen ? (
                <XClose className="h-5 w-5" />
              ) : (
                <SearchLg className="h-5 w-5" />
              )
            }
            disabled={false}
            className="h-10 w-10 md:h-12 md:w-12"
            onClick={() => {
              if (isMobileSearchOpen) {
                closeMobileSearch();
              } else {
                openMobileSearch();
                // Focus the textarea after overlay shows
                setTimeout(() => {
                  const textarea = document.getElementById(
                    "userPrompt"
                  ) as HTMLTextAreaElement;
                  if (textarea) {
                    textarea.focus();
                  }
                }, 100);
              }
            }}
          />
        )}

        {!mobileIconOnly && (
          <div
            className={twMerge(
              isHomepage ? "block w-full max-md:mx-4" : "hidden w-full lg:block"
            )}
          >
            {/* Input Container */}
            <div
              className={twMerge(
                "group relative flex items-center gap-3 rounded-4xl border p-1 transition-all duration-300 ease-in-out",
                isHomepage
                  ? "w-full border-tertiary-200/50 bg-white dark:border-white/20 dark:bg-white/10 dark:hover:border-white/40"
                  : "border-white/10 bg-white/10 dark:border-white/10 dark:bg-white/10",
                !isHomepage && "w-full"
              )}
              role="none"
              onClick={handleContainerClick("sendPrompt", "userPrompt")}
            >
              <div
                className={twMerge(
                  "flex flex-1 items-center px-3",
                  isHomepage ? "lg:py-3" : "lg:py-1"
                )}
              >
                {/* By default, Hotjar suppresses user input */}
                <textarea
                  ref={ref}
                  id="userPrompt"
                  data-testid="userPrompt"
                  tabIndex={0}
                  placeholder={t("OmniSearch.placeholder")}
                  aria-label={t("OmniSearch.placeholder")}
                  aria-multiline="true"
                  className={twMerge(
                    "field-sizing-content w-full resize-none border-none bg-transparent outline-none focus:ring-0",
                    isHomepage
                      ? "text-tertiary-900 placeholder-tertiary-500 dark:text-tertiary-100 dark:placeholder-white/80"
                      : "text-white/80 placeholder-white/80 dark:text-white/80 dark:placeholder-white/80",
                    "transition-all duration-300 ease-in-out",
                    "custom-scrollbar overflow-y-auto",
                    "h-5 max-h-20",
                    "leading-[120%]"
                  )}
                  value={value}
                  onChange={handleChange}
                  onKeyDown={onKeyDown}
                  onFocus={handleFocus}
                  disabled={isDisabled}
                />
              </div>

              <div className="flex-shrink-0 self-start">
                <IconButton
                  ariaLabel={t("OmniSearch.sendMessage")}
                  id="sendPrompt"
                  icon={
                    isSending ? (
                      <Loader data-testid="search-loading-indicator" />
                    ) : (
                      <SearchLg className="h-5 w-5 lg:h-6 lg:w-6" />
                    )
                  }
                  disabled={isDisabled || !(value || "").trim()}
                  className={twMerge(
                    "transition-all duration-200",
                    isHomepage
                      ? "bg-primary-500 text-primary-950 group-hover:bg-primary-400 hover:bg-primary-400 hover:text-primary-950 dark:bg-white/5 dark:text-white dark:group-hover:bg-white/10 dark:hover:text-white"
                      : "h-[38px] w-[38px]", // set fixed size to prevent layout difference with topbar on all pages

                    isSending && "cursor-not-allowed bg-white dark:bg-white/10"
                  )}
                  onClick={handleSend}
                />
              </div>
            </div>

            {/* Render children when expanded */}
            {shouldShowDropdown && children && (
              <div
                className={twMerge(
                  // Center horizontally relative to viewport, not just input width
                  "absolute top-full left-1/2 z-10 w-full -translate-x-1/2 bg-secondary-900 lg:rounded-2xl dark:bg-secondary-950",
                  // Enforce a responsive minimum width on larger breakpoints while respecting available space
                  "md:min-w-2xl",
                  // Smooth appearance animation (non-homepage only)
                  !isHomepage && "transition-all duration-300 ease-in-out",
                  getDropdownAnimationClasses()
                )}
                data-testid="search-drawer"
              >
                <div className="w-full px-4 lg:px-16">
                  {children(shouldShowDropdown)}
                </div>
              </div>
            )}
          </div>
        )}

        {isMobileSearchOpen && (
          <div className="lg:hidden">
            <div className="fixed inset-x-0 top-0 z-50 flex h-full flex-col bg-secondary-900 dark:bg-secondary-950">
              <div className="flex-1 overflow-y-auto px-4 pb-4">
                <div className="mb-6 pt-20">
                  <div
                    className="group relative flex items-center gap-3 rounded-4xl border border-white/10 bg-white/10 p-1"
                    role="none"
                    onClick={handleContainerClick(
                      "sendPrompt-mobile-overlay",
                      "userPrompt"
                    )}
                  >
                    <div className="flex flex-1 items-center px-3 py-3">
                      {/* By default, Hotjar suppresses user input */}
                      <textarea
                        id="userPrompt"
                        data-testid="userPrompt-mobile"
                        tabIndex={0}
                        placeholder={t("OmniSearch.placeholder")}
                        aria-label={t("OmniSearch.placeholder")}
                        aria-multiline="true"
                        className="field-sizing-content h-5 max-h-20 w-full resize-none border-none bg-transparent leading-[120%] text-white/80 placeholder-white/80 outline-none focus:ring-0"
                        value={value}
                        onChange={handleChange}
                        onKeyDown={onKeyDown}
                        disabled={isDisabled}
                      />
                    </div>
                    <div className="flex-shrink-0 self-start">
                      <IconButton
                        ariaLabel={t("OmniSearch.sendMessage")}
                        id="sendPrompt-mobile-overlay"
                        icon={
                          isSending ? (
                            <Loader data-testid="search-loading-indicator-mobile" />
                          ) : (
                            <SearchLg className="h-5 w-5" />
                          )
                        }
                        disabled={isDisabled || !(value || "").trim()}
                        className=""
                        onClick={handleSend}
                      />
                    </div>
                  </div>
                </div>

                {/* Scrollable Search Results/Suggestions */}
                {children && isMobileSearchOpen && (
                  <div className="flex-1 overflow-y-auto">{children(true)}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
);

// Set a custom display name for better readability in React DevTools (useful with forwardRef)
SearchInput.displayName = "SearchInput";
